import classNames from 'classnames';
import { type CSSProperties, type ReactNode, useId } from 'react';

import { css } from '@sorare/css';

import { GameplayTier } from '#src/__generated__/globalTypes.ts';
import { BrandXXS } from '#src/atoms/typography/index.tsx';
import { useGameplayTierRatings } from '#src/hooks/useGameplayTierRatings.ts';
import { range } from '#src/lib/arrays.ts';
import {
  GAMEPLAY_TIER_LABELS,
  nbaGameplayTierProgress,
  nextGameplayTier,
  previousGameplayTier,
} from '#src/lib/gameplayTiers.ts';

import {
  type Point,
  distanceToTopBorder,
  NEXT_TIER_REGION,
  pointAlongTopSides,
  PREVIOUS_TIER_REGION,
  TOP_BORDER_PATH,
} from './topBorder.ts';

const GAMEPLAY_TIER_COLORS: Record<GameplayTier, string> = {
  [GameplayTier.GOAT]: 'var(--c-score-high)',
  [GameplayTier.STAR_PLUS]: 'var(--c-green-400)',
  [GameplayTier.STAR]: 'var(--c-green-400)',
  [GameplayTier.IMPACT_PLUS]: 'var(--c-score-medium)',
  [GameplayTier.IMPACT]: 'var(--c-score-medium)',
  [GameplayTier.ROSTER_PLUS]: 'var(--c-yellow-400)',
  [GameplayTier.ROSTER]: 'var(--c-yellow-400)',
  [GameplayTier.DNP_PLUS]: 'var(--c-score-low)',
  [GameplayTier.DNP]: 'var(--c-score-low)',
};

const HEXAGON_PATH =
  'M13.1132 0.75338C14.8996 -0.25113 17.1004 -0.25113 18.8868 0.75338L28.1133 5.94173C29.8996 6.94623 31 8.80264 31 10.8117V21.1883C31 23.1974 29.8996 25.0538 28.1133 26.0583L18.8868 31.2466C17.1004 32.2511 14.8996 32.2511 13.1132 31.2466L3.88675 26.0583C2.10042 25.0538 1 23.1974 1 21.1883V10.8117C1 8.80264 2.10042 6.94623 3.88675 5.94173L13.1132 0.75338Z';

const OVER_CAP_COLOR = 'var(--c-red-400)';

const STAR_COLORS = {
  default: { stops: ['var(--c-white)', '#FCFF63', '#C26900'], glow: '#FCFF72' },
  overCap: {
    stops: ['var(--c-red-200)', 'var(--c-red-400)', 'var(--c-red-500)'],
    glow: OVER_CAP_COLOR,
  },
};

const TOP_BORDER_FADE = { opaqueUntil: 13.25, transparentFrom: 16.22 };

const STAR_PATH =
  'M6.67056 3.19975C6.76221 3.00107 7.04459 3.00107 7.13623 3.19975L8.08245 5.25115C8.1198 5.33212 8.19654 5.38788 8.28509 5.39838L10.5285 5.66437C10.7458 5.69013 10.833 5.95869 10.6724 6.10725L9.01379 7.64107C8.94832 7.70161 8.919 7.79182 8.93638 7.87929L9.37666 10.0951C9.4193 10.3097 9.19085 10.4757 8.99992 10.3688L7.02864 9.26535C6.95082 9.22179 6.85597 9.22179 6.77815 9.26535L4.80687 10.3688C4.61594 10.4757 4.38749 10.3097 4.43014 10.0951L4.87041 7.87929C4.88779 7.79182 4.85848 7.70161 4.79301 7.64107L3.13441 6.10725C2.97377 5.95869 3.06103 5.69013 3.27831 5.66437L5.5217 5.39838C5.61025 5.38788 5.68699 5.33212 5.72434 5.25115L6.67056 3.19975Z';

const STAR_OUTLINE_PATH =
  'M6.33358 2.92739C6.59957 2.52168 7.20725 2.52167 7.47323 2.92739L7.52401 3.02114L8.43026 4.98501L10.5787 5.23989C11.158 5.30858 11.3906 6.02437 10.9625 6.42055L9.37362 7.89028L9.7955 10.0114C9.90918 10.5835 9.30065 11.0264 8.79159 10.7418L6.90292 9.68423L5.01522 10.7418C4.50616 11.0264 3.89763 10.5835 4.01132 10.0114L4.43124 7.8893L2.84433 6.42055C2.4162 6.02437 2.64879 5.30858 3.22812 5.23989L5.37558 4.98501L6.2828 3.02114L6.33358 2.92739Z';

const STAR_CENTER_X = 6.9034;

const BADGE_STAR_LAYOUTS: Record<number, Point[]> = {
  1: [[9.0966, 8.9277]],
  2: [
    [4.0966, 8.9277],
    [14.6266, 8.9277],
  ],
  3: [
    [9.0966, 2.9277],
    [4.0966, 12.2977],
    [14.0966, 12.2977],
  ],
  4: [
    [3.5966, 4.9277],
    [14.4966, 4.9277],
    [3.5966, 14.8178],
    [14.4966, 14.8178],
  ],
  5: [
    [8.8566, 1.4048],
    [0.8966, 6.6047],
    [17.0165, 6.6047],
    [4.0166, 15.8648],
    [14.3766, 15.8648],
  ],
};

const STAR_GLOW_SPREAD = 1.098;

const INLINE = {
  height: 16,
  padding: 4,
  minWidth: 24,
  starSize: 8,
  starGap: 0,
  starScale: 0.8,
  glowSpread: STAR_GLOW_SPREAD / 2,
  starOffset: [-1.523, -1.376] as Point,
};

const BADGE_ASPECT_RATIO = 60 / 64;
const BADGE_VIEWBOX_HEIGHT = 32;
const BADGE_VIEWBOX_CENTER: Point = [16, 16];
const DEFAULT_BADGE_HEIGHT = 32;

const progressOf = (gameplayTier: GameplayTier, l10Score?: number | null) =>
  typeof l10Score === 'number'
    ? nbaGameplayTierProgress(gameplayTier, l10Score)
    : undefined;

// Tilt of the L10 tab: parallel to the top-left side, upright over the apex,
// or parallel to the top-right side, depending on which third the dot is in.
const L10_TAB_ANGLES = [-30, 0, 30];

const l10TabPlacementStyle = (height: number, progress = 0.5) => {
  const angle =
    L10_TAB_ANGLES[
      Math.min(
        Math.floor(progress * L10_TAB_ANGLES.length),
        L10_TAB_ANGLES.length - 1
      )
    ];
  const distance =
    distanceToTopBorder(BADGE_VIEWBOX_CENTER, angle) *
    (height / BADGE_VIEWBOX_HEIGHT);
  return {
    '--l10-tab-angle': `${angle}deg`,
    '--l10-tab-distance': `${distance}px`,
  } as CSSProperties;
};

const badge = css`
  display: block;
  overflow: visible;
  /* Own stacking context, so an overflowing badge paints above the footer rows
     below instead of showing their divider through its notches. */
  isolation: isolate;
`;

const l10TabRoot = css`
  --l10-tab-shown: 1;
  position: relative;
  width: fit-content;

  @media (hover: hover) {
    --l10-tab-shown: 0;

    &.pinned {
      --l10-tab-shown: 1;
    }

    &:hover {
      --l10-tab-shown: 1;
      --l10-dot-scale: 1.4;
    }
  }
`;

const raisedBadge = css`
  position: relative;
  z-index: 1;
`;

const l10TabAnchor = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: rotate(var(--l10-tab-angle));
`;

const l10TabBox = css`
  position: absolute;
  bottom: var(--l10-tab-distance);
  left: 0;
  display: flex;
  justify-content: center;
  min-width: var(--triple-unit);
  padding: 0 var(--half-unit) var(--half-unit);
  background-color: var(--c-nd-50-opaque);
  border-radius: 8px 8px 0 0;
  opacity: var(--l10-tab-shown);
  transform: translate(-50%, calc((1 - var(--l10-tab-shown)) * 100%));

  @media (prefers-reduced-motion: no-preference) {
    transition:
      transform 250ms var(--ease-out-quart),
      opacity 250ms var(--ease-out-quart);
  }
`;

const l10TabBoxOverCap = css`
  & .text-gradient-color {
    --text-gradient-color: var(--c-red-400) !important;
  }
`;

const progressDotStyle = css`
  transform-box: fill-box;
  transform-origin: center;
  transform: scale(var(--l10-dot-scale, 1));

  @media (prefers-reduced-motion: no-preference) {
    transition: transform 250ms var(--ease-out-quart);
  }
`;

const inline = css`
  display: inline-block;
  vertical-align: middle;
`;

const useStarIds = () => {
  const id = useId();
  return {
    gradient: `${id}-star-gradient`,
    glow: `${id}-star-glow`,
    halfMask: `${id}-half-star`,
  };
};

type StarIds = ReturnType<typeof useStarIds>;

const StarDefs = ({
  ids,
  glowSpread = STAR_GLOW_SPREAD,
  overCap,
}: {
  ids: StarIds;
  glowSpread?: number;
  overCap?: boolean;
}) => {
  const colors = overCap ? STAR_COLORS.overCap : STAR_COLORS.default;
  return (
    <>
      <linearGradient
        id={ids.gradient}
        x1="11.3107"
        y1="2.921"
        x2="3.7404"
        y2="12.6783"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor={colors.stops[0]} />
        <stop offset="0.352" stopColor={colors.stops[1]} />
        <stop offset="1" stopColor={colors.stops[2]} />
      </linearGradient>
      <filter
        id={ids.glow}
        x="-50%"
        y="-50%"
        width="200%"
        height="200%"
        colorInterpolationFilters="sRGB"
      >
        <feDropShadow
          dx="0"
          dy="0"
          stdDeviation={glowSpread}
          floodColor={colors.glow}
          floodOpacity="0.6"
        />
      </filter>
      <mask id={ids.halfMask} maskUnits="userSpaceOnUse">
        <path d={STAR_PATH} fill="white" />
        <path d={STAR_OUTLINE_PATH} stroke="white" strokeWidth="0.855" />
      </mask>
    </>
  );
};

type TierStarProps = {
  ids: StarIds;
  transform: string;
  half: boolean;
};

const TierStar = ({ ids, transform, half }: TierStarProps) => (
  <g transform={transform}>
    <g filter={`url(#${ids.glow})`}>
      <path d={STAR_PATH} fill={`url(#${ids.gradient})`} />
      <path
        d={STAR_OUTLINE_PATH}
        stroke="#1D1D1D"
        strokeOpacity="0.5"
        strokeWidth="0.855"
      />
    </g>
    {half ? (
      <rect
        x={STAR_CENTER_X}
        y="0"
        width="7"
        height="14"
        fill="var(--c-black)"
        fillOpacity="0.8"
        mask={`url(#${ids.halfMask})`}
      />
    ) : null}
  </g>
);

const useStarLayout = (gameplayTier: GameplayTier) => {
  const starValue = useGameplayTierRatings().starsOf(gameplayTier);
  if (starValue === undefined) {
    return undefined;
  }

  const count = Math.ceil(starValue);
  const lastStarIsHalf = !Number.isInteger(starValue);
  return {
    count,
    isHalf: (index: number) => lastStarIsHalf && index === count - 1,
  };
};

const tierColorStyle = (gameplayTier: GameplayTier, overCap?: boolean) =>
  ({
    '--gameplay-tier-color': overCap
      ? OVER_CAP_COLOR
      : GAMEPLAY_TIER_COLORS[gameplayTier],
  }) as CSSProperties;

type CommonProps = {
  gameplayTier: GameplayTier;
  height?: number;
  className?: string;
  /** Picking this player would bust the lineup's star cap. */
  overCap?: boolean;
};

type BadgeProps = CommonProps & {
  l10Score?: number | null;
  /** Revealed above the progress dot when hovering the badge. */
  l10Tab?: ReactNode;
  /** Keeps the tab shown and upright instead of revealing it on hover. */
  pinnedL10Tab?: boolean;
};

const HexagonBadge = ({
  gameplayTier,
  height = DEFAULT_BADGE_HEIGHT,
  className,
  l10Score,
  l10Tab,
  pinnedL10Tab,
  overCap,
}: BadgeProps) => {
  const id = useId();
  const starIds = useStarIds();
  const ids = {
    borderFade: `${id}-border-fade`,
    borderFadeGradient: `${id}-border-fade-gradient`,
    progressGlow: `${id}-progress-glow`,
    previousTierRegion: `${id}-previous-tier-region`,
    nextTierRegion: `${id}-next-tier-region`,
  };

  const stars = useStarLayout(gameplayTier);
  if (!stars) {
    return null;
  }

  const progress = progressOf(gameplayTier, l10Score);
  const progressDot =
    progress === undefined ? undefined : pointAlongTopSides(progress);
  const neighbourTiers =
    progress === undefined || overCap
      ? []
      : [
          {
            tier: previousGameplayTier(gameplayTier),
            region: ids.previousTierRegion,
          },
          { tier: nextGameplayTier(gameplayTier), region: ids.nextTierRegion },
        ];

  const showL10Tab = l10Tab !== undefined;

  const svg = (
    <svg
      role="img"
      aria-label={GAMEPLAY_TIER_LABELS[gameplayTier]}
      className={classNames(badge, { [raisedBadge]: showL10Tab }, className)}
      style={tierColorStyle(gameplayTier, overCap)}
      height={height}
      width={Math.round(height * BADGE_ASPECT_RATIO)}
      viewBox="1 0 30 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <StarDefs ids={starIds} overCap={overCap} />
        <linearGradient
          id={ids.borderFadeGradient}
          x1="0"
          y1={TOP_BORDER_FADE.opaqueUntil}
          x2="0"
          y2={TOP_BORDER_FADE.transparentFrom}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" />
          <stop offset="1" stopColor="white" stopOpacity="0" />
        </linearGradient>
        <mask
          id={ids.borderFade}
          maskUnits="userSpaceOnUse"
          x="0"
          y="-1"
          width="32"
          height="18"
        >
          <rect
            x="0"
            y="-1"
            width="32"
            height="18"
            fill={`url(#${ids.borderFadeGradient})`}
          />
        </mask>
        <clipPath id={ids.previousTierRegion}>
          <polygon points={PREVIOUS_TIER_REGION} />
        </clipPath>
        <clipPath id={ids.nextTierRegion}>
          <polygon points={NEXT_TIER_REGION} />
        </clipPath>
        <filter
          id={ids.progressGlow}
          filterUnits="userSpaceOnUse"
          x="-4"
          y="-4"
          width="40"
          height="24"
          colorInterpolationFilters="sRGB"
        >
          <feDropShadow
            dx="0"
            dy="0"
            stdDeviation="2"
            floodColor="var(--gameplay-tier-color)"
          />
        </filter>
      </defs>

      <path d={HEXAGON_PATH} fill="var(--gameplay-tier-color)" />
      <path
        d={HEXAGON_PATH}
        fill="var(--c-black)"
        fillOpacity={overCap ? 0.8 : 0.6}
      />

      {BADGE_STAR_LAYOUTS[stars.count].map(([x, y], index) => (
        <TierStar
          key={`${x}-${y}`}
          ids={starIds}
          transform={`translate(${x} ${y})`}
          half={stars.isHalf(index)}
        />
      ))}

      <g mask={`url(#${ids.borderFade})`} strokeWidth="1.6">
        <path d={TOP_BORDER_PATH} stroke="var(--gameplay-tier-color)" />
        {neighbourTiers.map(({ tier, region }) =>
          tier ? (
            <path
              key={region}
              d={TOP_BORDER_PATH}
              stroke={GAMEPLAY_TIER_COLORS[tier]}
              clipPath={`url(#${region})`}
            />
          ) : null
        )}
      </g>

      {progressDot ? (
        <circle
          className={progressDotStyle}
          cx={progressDot[0]}
          cy={progressDot[1]}
          r="1.25"
          fill="var(--gameplay-tier-color)"
          stroke="var(--c-white)"
          strokeWidth="0.5"
          filter={`url(#${ids.progressGlow})`}
        />
      ) : null}
    </svg>
  );

  if (!showL10Tab) {
    return svg;
  }

  return (
    <div className={classNames(l10TabRoot, { pinned: pinnedL10Tab })}>
      <div
        className={l10TabAnchor}
        style={l10TabPlacementStyle(
          height,
          pinnedL10Tab ? undefined : progress
        )}
      >
        <BrandXXS
          as="div"
          className={classNames(l10TabBox, { [l10TabBoxOverCap]: overCap })}
        >
          {l10Tab}
        </BrandXXS>
      </div>
      {svg}
    </div>
  );
};

const inlineWidth = (starCount: number) =>
  Math.max(
    INLINE.minWidth,
    2 * INLINE.padding +
      starCount * INLINE.starSize +
      (starCount - 1) * INLINE.starGap
  );

const InlineBadge = ({
  gameplayTier,
  height = INLINE.height,
  className,
  overCap,
}: CommonProps) => {
  const starIds = useStarIds();
  const stars = useStarLayout(gameplayTier);
  if (!stars) {
    return null;
  }

  const width = inlineWidth(stars.count);
  const starsWidth =
    stars.count * INLINE.starSize + (stars.count - 1) * INLINE.starGap;
  const firstStarX = (width - starsWidth) / 2;
  const [offsetX, offsetY] = INLINE.starOffset;
  const starY = Math.round(INLINE.padding + offsetY);

  return (
    <svg
      role="img"
      aria-label={GAMEPLAY_TIER_LABELS[gameplayTier]}
      className={classNames(inline, className)}
      style={tierColorStyle(gameplayTier, overCap)}
      height={height}
      width={(height * width) / INLINE.height}
      viewBox={`0 0 ${width} ${INLINE.height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <StarDefs
          ids={starIds}
          glowSpread={INLINE.glowSpread}
          overCap={overCap}
        />
      </defs>

      <rect
        x="0.5"
        y="0.5"
        width={width - 1}
        height={INLINE.height - 1}
        rx={(INLINE.height - 1) / 2}
        fill="var(--gameplay-tier-color)"
      />
      <rect
        x="0.5"
        y="0.5"
        width={width - 1}
        height={INLINE.height - 1}
        rx={(INLINE.height - 1) / 2}
        fill="var(--c-black)"
        fillOpacity="0.6"
        stroke="var(--gameplay-tier-color)"
      />

      {range(stars.count).map(index => {
        const x = Math.round(
          firstStarX + index * (INLINE.starSize + INLINE.starGap) + offsetX
        );
        return (
          <TierStar
            key={x}
            ids={starIds}
            transform={`translate(${x} ${starY}) scale(${INLINE.starScale})`}
            half={stars.isHalf(index)}
          />
        );
      })}
    </svg>
  );
};

type Props =
  | (BadgeProps & { variant?: 'badge' })
  | (CommonProps & {
      variant: 'inline';
      l10Score?: never;
      l10Tab?: never;
      pinnedL10Tab?: never;
    });

export const GameplayTierBadge = ({ variant, ...props }: Props) =>
  variant === 'inline' ? (
    <InlineBadge {...props} />
  ) : (
    <HexagonBadge {...props} />
  );
